import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import {
  BugOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  NodeIndexOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { diagnoseOrder, checkDay, routeHealth, traceExternalId } from '../modules/diagnostics/api';
import { resendIntake } from '../modules/partner-intake/api';

const { Paragraph } = Typography;

// MCP admin assistant Phase 1 — the in-planner face of the diagnostics
// engine. Same three read-only checks the MCP tools expose.

const SEVERITY_COLOR = { blocker: 'red', warning: 'orange', info: 'blue' };

const FindingsList = ({ findings, t }) => {
  if (!findings || findings.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
      {findings.map((f, i) => (
        <Alert
          key={i}
          type={f.severity === 'blocker' ? 'error' : f.severity === 'warning' ? 'warning' : 'info'}
          showIcon
          message={
            <span>
              <Tag color={SEVERITY_COLOR[f.severity]}>{t(`diagnostics.severity.${f.severity}`)}</Tag>
              <Tag>{f.code}</Tag>
              {f.message}
            </span>
          }
        />
      ))}
    </div>
  );
};

// OVO-292 — the trace verdict → Alert type + the timeline dot colour.
const VERDICT_TYPE = {
  found: 'success',
  trashed: 'warning',
  refused: 'error',
  upload_failed: 'error',
  refused_by_ovo_api: 'error',
  received_not_uploaded: 'warning',
  not_found: 'info',
  shared_id: 'warning',
};
const TONE_COLOR = { ok: 'green', warn: 'orange', error: 'red', info: 'gray' };
const fmtTs = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm:ss') : '—');

/**
 * OVO-292 — "Trace by external id": one search box, every ledger the planner
 * keeps. Born from the BUT-1201 incident (an order the partner sent, ovo-api
 * forwarded, the planner refused for a good reason — and nobody could see
 * any of that without gcloud). The Resend lives right here so the operator
 * fixes the mapping, resends, re-traces without leaving the page.
 */
const TraceView = ({ result, t, onResent, onPickPartner }) => {
  const [resendingId, setResendingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  if (!result) return null;
  const { summary, orders, intakes, timeline, counts } = result;
  // Sep 2026 — the same external id can belong to several partners (ET 39142
  // and BEAN 39142): one verdict per partner, and a way to narrow down.
  const partners = result.partners || [];

  const handleResend = async (intake) => {
    setResendingId(intake.id);
    try {
      const { response, error } = await resendIntake(intake.id);
      if (error) throw new Error(error.message || t('common:error'));
      const okStatus = response?.status >= 200 && response.status < 300;
      (okStatus ? messageApi.success : messageApi.warning)(
        t('apiIntake.resendStatus', { status: response?.status ?? '—' }),
      );
      onResent?.();
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setResendingId(null);
    }
  };

  const countTags = [
    ['orders', counts.orders],
    ['outcomes', counts.outcomes],
    ['intakes', counts.intakes],
    ['webhooks', counts.webhooks],
    ['airtable', counts.airtable],
    ['audits', counts.audits],
    ['tickets', counts.tickets],
  ];

  return (
    <div style={{ marginTop: 16 }} data-testid="trace-result">
      {contextHolder}
      <Alert
        type={VERDICT_TYPE[summary.verdict] || 'info'}
        showIcon
        message={
          <span>
            <Tag>{t(`diagnostics.traceVerdict.${summary.verdict}`)}</Tag>
            {summary.headline}
          </span>
        }
        description={summary.nextStep || undefined}
      />
      {partners.length > 1 && (
        <Card
          size="small"
          style={{ marginTop: 8 }}
          title={t('diagnostics.tracePartnersTitle')}
          data-testid="trace-partners"
        >
          {partners.map((p) => (
            <div
              key={p.code}
              style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 0' }}
            >
              <Tag color="blue">{p.code}</Tag>
              <Tag
                color={
                  VERDICT_TYPE[p.verdict] === 'success'
                    ? 'green'
                    : VERDICT_TYPE[p.verdict] === 'error'
                      ? 'red'
                      : 'orange'
                }
              >
                {t(`diagnostics.traceVerdict.${p.verdict}`)}
              </Tag>
              <span style={{ flex: 1, minWidth: 200 }}>{p.headline}</span>
              <Button size="small" onClick={() => onPickPartner?.(p.code)} data-testid={`trace-partner-${p.code}`}>
                {t('diagnostics.tracePartnerOnly', { code: p.code })}
              </Button>
            </div>
          ))}
        </Card>
      )}
      {result.partner && (
        <div style={{ marginTop: 8, color: 'var(--text-muted)' }}>
          <Tag color="blue">{result.partner}</Tag>
          {t('diagnostics.traceFilteredOn', { code: result.partner })} ·{' '}
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onPickPartner?.(null)}>
            {t('diagnostics.traceShowAllPartners')}
          </Button>
        </div>
      )}
      <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {countTags.map(([k, n]) => (
          <Tag key={k} color={n > 0 ? 'blue' : undefined}>
            {n} {t(`diagnostics.traceCounts.${k}`)}
          </Tag>
        ))}
      </div>

      {orders.map((o) => (
        <Card
          key={o.orderCode}
          size="small"
          style={{ marginTop: 12 }}
          title={
            <span>
              <Link to={`/admin/orders/${o.orderCode}`}>{o.orderCode}</Link>
              <Tag className={`status order ${String(o.status).toLowerCase()}`} style={{ marginLeft: 8 }}>
                {o.status}
              </Tag>
              {o.trashed && <Tag color="red">{t('diagnostics.trashed')}</Tag>}
            </span>
          }
          extra={
            <Link to={`/admin/diagnostics?mode=order&orderCode=${encodeURIComponent(o.orderCode)}`}>
              {t('diagnostics.traceDiagnoseLink')}
            </Link>
          }
        >
          <Descriptions size="small" column={3} bordered>
            <Descriptions.Item label={t('diagnostics.name')}>{o.name || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('diagnostics.date')}>{o.deliveryDate || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('diagnostics.partner')}>{o.partnerRef?.code || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('diagnostics.traceIds')}>
              {o.partnerOrderRef && <Tag>{o.partnerOrderRef}</Tag>}
              {o.orderRef && <Tag>{o.orderRef}</Tag>}
              {o.airtableOrderRecordRef && <Tag>{o.airtableOrderRecordRef}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('diagnostics.traceTour')}>{o.tourRef || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('diagnostics.routeCol')}>
              {o.routeId ? <Link to={`/admin/routes/${o.routeId}`}>#{o.routeId}</Link> : '—'}
              {o.route?.driver?.name ? ` · ${o.route.driver.name}` : ''}
            </Descriptions.Item>
          </Descriptions>
          {o.diagnosis && <FindingsList findings={o.diagnosis.findings} t={t} />}
        </Card>
      ))}

      {intakes.map((it) => {
        const errors = (it.matchedResults || []).filter((r) => r.error);
        return (
          <Card
            key={it.id}
            size="small"
            style={{ marginTop: 12 }}
            title={
              <span>
                {t('diagnostics.traceIntakeTitle', { id: it.id, code: it.partnerCode, count: it.orderCount })}
                <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontWeight: 400 }}>
                  {fmtTs(it.createdAt)}
                </span>
              </span>
            }
            extra={
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <Link to={`/admin/api-intake?partnerCode=${encodeURIComponent(it.partnerCode)}`}>
                  {t('diagnostics.traceOpenIntake')}
                </Link>
                <Popconfirm
                  title={t('diagnostics.traceResendConfirm', { count: it.orderCount, code: it.partnerCode })}
                  onConfirm={() => handleResend(it)}
                  okText={t('apiIntake.resend')}
                  cancelText={t('common:cancel')}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={resendingId === it.id}
                    data-testid={`trace-resend-${it.id}`}
                  >
                    {t('apiIntake.resend')}
                  </Button>
                </Popconfirm>
              </span>
            }
          >
            {it.hasResults ? (
              errors.length > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={t('diagnostics.traceOvoApiAnswer')}
                  description={errors.map((r, i) => (
                    <div key={i}>
                      <b>{r.partner_order_id}</b>: {r.error}
                    </div>
                  ))}
                />
              ) : (
                <Alert type="success" showIcon style={{ marginBottom: 8 }} message={t('diagnostics.traceOvoApiOk')} />
              )
            ) : (
              <Alert type="info" showIcon style={{ marginBottom: 8 }} message={t('diagnostics.traceOvoApiUnknown')} />
            )}
            <Paragraph
              code
              style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto', fontSize: 12, marginBottom: 0 }}
            >
              {JSON.stringify(it.matchedItems, null, 2)}
            </Paragraph>
          </Card>
        );
      })}

      {timeline.length > 0 && (
        <Card size="small" style={{ marginTop: 12 }} title={t('diagnostics.traceTimeline')}>
          <Timeline
            items={timeline.map((e) => ({
              color: TONE_COLOR[e.tone] || 'gray',
              children: (
                <div>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)', marginRight: 8 }}>
                    {fmtTs(e.at)}
                  </span>
                  <Tag>{t(`diagnostics.traceKind.${e.kind}`)}</Tag>
                  {e.link?.type === 'order' && e.link.id ? (
                    <Link to={`/admin/orders/${e.link.id}`}>{e.title}</Link>
                  ) : e.link?.type === 'ticket' ? (
                    <Link to={`/admin/tickets/${e.link.id}`}>{e.title}</Link>
                  ) : (
                    e.title
                  )}
                  {e.by && <span style={{ color: 'var(--text-muted)' }}> · {e.by}</span>}
                  {e.detail && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{e.detail}</div>}
                </div>
              ),
            }))}
          />
        </Card>
      )}
    </div>
  );
};

const DiagnosticsPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();
  const mode = searchParams.get('mode') || 'trace';
  const [loading, setLoading] = useState(false);
  const [traceQuery, setTraceQuery] = useState(searchParams.get('q') || '');
  const [traceResult, setTraceResult] = useState(null);

  const [orderCode, setOrderCode] = useState(searchParams.get('orderCode') || '');
  const [orderResult, setOrderResult] = useState(null);
  const [dayValue, setDayValue] = useState(dayjs());
  const [dayResult, setDayResult] = useState(null);
  const [routeId, setRouteId] = useState(null);
  const [routeResult, setRouteResult] = useState(null);

  const setMode = (m) => {
    const next = new URLSearchParams(searchParams);
    next.set('mode', m);
    setSearchParams(next, { replace: true });
  };

  const run = async (fn, setter) => {
    setLoading(true);
    try {
      const { response, error } = await fn();
      if (error) throw new Error(error.message || t('common:error'));
      setter(response);
    } catch (e) {
      setter(null);
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setLoading(false);
    }
  };

  // `partner` (Sep 2026): undefined keeps the current URL filter, null clears
  // it, a code narrows the trace to that partner. A new id typed in the box
  // always starts unfiltered.
  const runTrace = (q, partner) => {
    const value = (q ?? traceQuery).trim();
    if (!value) return;
    const next = new URLSearchParams(searchParams);
    const keepFilter = partner === undefined && value === searchParams.get('q');
    const partnerCode = partner === undefined ? (keepFilter ? searchParams.get('partner') : null) : partner;
    next.set('mode', 'trace');
    next.set('q', value);
    if (partnerCode) next.set('partner', partnerCode);
    else next.delete('partner');
    setSearchParams(next, { replace: true });
    run(() => traceExternalId(value, partnerCode || undefined), setTraceResult);
  };

  // Deep link: /admin/diagnostics?q=<id>[&partner=<code>] runs the trace on load.
  useEffect(() => {
    const q = searchParams.get('q');
    if (mode === 'trace' && q && !traceResult && !loading) runTrace(q, searchParams.get('partner') || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderIssueColumns = [
    {
      title: t('diagnostics.order'),
      key: 'orderCode',
      render: (r) => <Link to={`/admin/orders/${r.orderCode}`}>{r.orderCode}</Link>,
    },
    { title: t('diagnostics.name'), dataIndex: 'name', key: 'name' },
    { title: t('diagnostics.partner'), dataIndex: 'partner', key: 'partner' },
    {
      title: t('diagnostics.findings'),
      key: 'findings',
      render: (r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {r.findings.map((f, i) => (
            <span key={i}>
              <Tag color={SEVERITY_COLOR[f.severity]}>{f.code}</Tag>
              {f.message}
            </span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="DiagnosticsPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <BugOutlined style={{ marginRight: 8 }} />
              {t('diagnostics.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)' }}>{t('diagnostics.subtitle')}</div>
          </div>
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: 'trace', label: t('diagnostics.modeTrace'), icon: <SearchOutlined /> },
              { value: 'order', label: t('diagnostics.modeOrder'), icon: <FileSearchOutlined /> },
              { value: 'day', label: t('diagnostics.modeDay'), icon: <CalendarOutlined /> },
              { value: 'route', label: t('diagnostics.modeRoute'), icon: <NodeIndexOutlined /> },
            ]}
          />
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {mode === 'trace' && (
            <>
              <Input
                style={{ width: 360 }}
                placeholder={t('diagnostics.tracePlaceholder')}
                value={traceQuery}
                onChange={(e) => setTraceQuery(e.target.value)}
                onPressEnter={() => runTrace()}
                allowClear
                data-testid="trace-input"
              />
              <Button type="primary" disabled={!traceQuery.trim()} onClick={() => runTrace()} data-testid="trace-run">
                {t('diagnostics.runTrace')}
              </Button>
              <span style={{ color: 'var(--text-muted)', alignSelf: 'center' }}>{t('diagnostics.traceHint')}</span>
            </>
          )}
          {mode === 'order' && (
            <>
              <Input
                style={{ width: 260 }}
                placeholder={t('diagnostics.orderCodePlaceholder')}
                value={orderCode}
                onChange={(e) => setOrderCode(e.target.value.trim())}
                onPressEnter={() => orderCode && run(() => diagnoseOrder(orderCode), setOrderResult)}
              />
              <Button
                type="primary"
                disabled={!orderCode}
                onClick={() => run(() => diagnoseOrder(orderCode), setOrderResult)}
              >
                {t('diagnostics.run')}
              </Button>
            </>
          )}
          {mode === 'day' && (
            <>
              <DatePicker value={dayValue} onChange={setDayValue} allowClear={false} />
              <Button type="primary" onClick={() => run(() => checkDay(dayValue.format('YYYY-MM-DD')), setDayResult)}>
                {t('diagnostics.runDay')}
              </Button>
            </>
          )}
          {mode === 'route' && (
            <>
              <InputNumber
                style={{ width: 200 }}
                placeholder={t('diagnostics.routeIdPlaceholder')}
                value={routeId}
                onChange={setRouteId}
                onPressEnter={() => routeId && run(() => routeHealth(routeId), setRouteResult)}
              />
              <Button
                type="primary"
                disabled={!routeId}
                onClick={() => run(() => routeHealth(routeId), setRouteResult)}
              >
                {t('diagnostics.run')}
              </Button>
            </>
          )}
        </div>

        <Spin spinning={loading}>
          {mode === 'trace' && (
            <TraceView
              result={traceResult}
              t={t}
              onResent={() => runTrace()}
              onPickPartner={(code) => runTrace(traceResult?.query, code)}
            />
          )}
          {mode === 'order' && orderResult && (
            <div style={{ marginTop: 16 }}>
              <Alert
                type={orderResult.canAssign ? 'success' : 'error'}
                showIcon
                message={
                  orderResult.canAssign
                    ? t('diagnostics.orderOk', { code: orderResult.order.orderCode })
                    : t('diagnostics.orderBlocked', { code: orderResult.order.orderCode })
                }
              />
              <Descriptions size="small" column={3} style={{ marginTop: 12 }} bordered>
                <Descriptions.Item label={t('diagnostics.status')}>
                  <Tag className={`status order ${String(orderResult.order.status).toLowerCase()}`}>
                    {orderResult.order.status}
                  </Tag>
                  {orderResult.order.trashed && <Tag color="red">{t('diagnostics.trashed')}</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.date')}>{orderResult.order.deliveryDate}</Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.routeCol')}>
                  {orderResult.order.routeId ? (
                    <Link to={`/admin/routes/${orderResult.order.routeId}`}>#{orderResult.order.routeId}</Link>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.partner')}>
                  {orderResult.order.partner?.code || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.types')}>
                  {(orderResult.order.types || []).map((x) => (
                    <Tag key={x}>{x}</Tag>
                  ))}
                  {(orderResult.order.types || []).length === 0 && '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.dayContext')}>
                  {t('diagnostics.dayContextValue', {
                    shifts: orderResult.day.shifts,
                    routes: orderResult.day.routes,
                  })}
                </Descriptions.Item>
              </Descriptions>
              {orderResult.optimizerHint && (
                <Alert
                  style={{ marginTop: 12 }}
                  type="info"
                  showIcon
                  message={t('diagnostics.optimizerHint')}
                  description={orderResult.optimizerHint}
                />
              )}
              <FindingsList findings={orderResult.findings} t={t} />
              {orderResult.findings.length === 0 && (
                <Alert style={{ marginTop: 12 }} type="success" showIcon message={t('diagnostics.noFindings')} />
              )}
            </div>
          )}

          {mode === 'day' && dayResult && (
            <div style={{ marginTop: 16 }}>
              <Alert
                type={dayResult.summary.ok ? 'success' : 'error'}
                showIcon
                message={
                  dayResult.summary.ok
                    ? t('diagnostics.dayOk', { date: dayResult.date })
                    : t('diagnostics.dayBlocked', { date: dayResult.date, count: dayResult.summary.blockers })
                }
                description={t('diagnostics.daySummary', {
                  orders: dayResult.summary.candidateOrders,
                  shifts: dayResult.summary.shifts,
                  routes: dayResult.summary.routes,
                  issues: dayResult.summary.ordersWithIssues,
                })}
              />
              <FindingsList findings={dayResult.findings} t={t} />
              {dayResult.orderIssues.length > 0 && (
                <Table
                  style={{ marginTop: 12 }}
                  rowKey="orderCode"
                  size="small"
                  columns={orderIssueColumns}
                  dataSource={dayResult.orderIssues}
                  pagination={{ pageSize: 20, hideOnSinglePage: true }}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </div>
          )}

          {mode === 'route' && routeResult && (
            <div style={{ marginTop: 16 }}>
              <Alert
                type={routeResult.healthy ? 'success' : 'warning'}
                showIcon
                message={
                  routeResult.healthy
                    ? t('diagnostics.routeOk', { id: routeResult.route.id })
                    : t('diagnostics.routeIssues', { id: routeResult.route.id })
                }
              />
              <Descriptions size="small" column={3} style={{ marginTop: 12 }} bordered>
                <Descriptions.Item label={t('diagnostics.routeCol')}>
                  <Link to={`/admin/routes/${routeResult.route.id}`}>#{routeResult.route.id}</Link>
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.date')}>{routeResult.route.date}</Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.driver')}>
                  {routeResult.route.driver?.name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.status')}>
                  {routeResult.route.status} / {routeResult.route.actionStatus}
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.stepsCol')}>
                  {routeResult.route.steps} ({routeResult.route.orders} {t('diagnostics.ordersWord')})
                </Descriptions.Item>
                <Descriptions.Item label={t('diagnostics.pause')}>
                  {routeResult.pause.pauseCount > 0
                    ? t('diagnostics.pauseValue', {
                        count: routeResult.pause.pauseCount,
                        mins: routeResult.pause.pausedTotalMins,
                      })
                    : '—'}
                  {routeResult.pause.openPause && (
                    <Tag color="orange" style={{ marginLeft: 6 }}>
                      {t('diagnostics.openPause')}
                    </Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>

              <Card size="small" title={t('diagnostics.stepIntegrity')} style={{ marginTop: 12 }}>
                {routeResult.stepIntegrity.duplicatesRemoved === 0 &&
                routeResult.stepIntegrity.stepsRecreated === 0 &&
                (routeResult.stepIntegrity.stepStatusFixed ?? 0) === 0 ? (
                  <Alert type="success" showIcon message={t('diagnostics.stepsOk')} />
                ) : (
                  <Alert
                    type="error"
                    showIcon
                    message={t('diagnostics.stepsDamaged', {
                      dupes: routeResult.stepIntegrity.duplicatesRemoved,
                      missing: routeResult.stepIntegrity.stepsRecreated,
                      statuses: routeResult.stepIntegrity.stepStatusFixed ?? 0,
                    })}
                    description={t('diagnostics.stepsRepairHint')}
                  />
                )}
              </Card>

              {routeResult.inconsistency && (
                <Alert
                  style={{ marginTop: 12 }}
                  type="warning"
                  showIcon
                  message={`${t('diagnostics.inconsistency')}: ${routeResult.inconsistency.type}`}
                  description={
                    <span>
                      {t('diagnostics.offBy', { mins: routeResult.inconsistency.offByMins })}
                      {routeResult.inconsistency.correction?.correctable && (
                        <Tag color="green" style={{ marginLeft: 8 }}>
                          {t('diagnostics.correctable')}
                        </Tag>
                      )}{' '}
                      <Link to="/admin/inconsistencies">{t('diagnostics.goToInconsistencies')}</Link>
                    </span>
                  }
                />
              )}
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default DiagnosticsPage;
